import { IRestApiService } from '../types';

export interface DictionaryItem {
  value: string;
  count: number;
  createdAt: Date;
}

export interface DictionaryItemDto {
  value: string;
  createdAt: Date;
  count: number;
  new: boolean;
}

export interface FunnelDictionary {
  _id: string;
  ownerId: string;
  sources?: DictionaryItem[];
  tags?: DictionaryItem[];
  createdAt?: Date;
  updatedAt?: Date;
}

export class FunnelDictionaryError {
  constructor(
    public error: 'duplicate-add' | 'duplicate-update' | 'items-is-in-use',
    public oldValue: string,
    public newValue: string,
    public affectedCount: number
  ) {}

  static instanceFromBody(body) {
    return new FunnelDictionaryError(body.error, body.oldValue, body.newValue, body.affectedCount);
  }
}

export type DictionaryKey = 'sources' | 'tags';

const basePath = 'pending-clients';

class FunnelDictionaryApi {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  async getDictionary(funnelId: string, dictionaryKey?: string): Promise<FunnelDictionary> {
    const params = new URLSearchParams({ funnelId });
    if (dictionaryKey) {
      params.append('dictionaryKey', dictionaryKey);
    }
    return this.restApiService.get(`${basePath}/dictionary?${params.toString()}`);
  }

  async getDictionaryValues(funnelId: string, dictionaryKey: string): Promise<string[]> {
    const params = new URLSearchParams({ funnelId, dictionaryKey });
    return this.restApiService.get(`${basePath}/dictionary/values?${params.toString()}`);
  }

  async remove(funnelId: string, dictionaryKey: DictionaryKey, body: { value: string; force: boolean }): Promise<void> {
    try {
      return await this.restApiService.delete(`${basePath}/dictionary/${funnelId}/${dictionaryKey}`, body);
    } catch (error: any) {
      if (error.status === 409) {
        throw FunnelDictionaryError.instanceFromBody(error.data);
      }
      throw error;
    }
  }

  async update(
    funnelId: string,
    dictionaryKey: DictionaryKey,
    body: { oldValue?: string; newValue: string; force?: boolean }
  ): Promise<DictionaryItemDto> {
    try {
      return await this.restApiService.put(`${basePath}/dictionary/${funnelId}/${dictionaryKey}`, body);
    } catch (error: any) {
      if (error.status === 409) {
        throw FunnelDictionaryError.instanceFromBody(error.data);
      }
      throw error;
    }
  }
}

export default FunnelDictionaryApi;
